import { zodResolver } from "@hookform/resolvers/zod";
import type { ColumnDef, Getter } from "@tanstack/react-table";
import type { AxiosError } from "axios";
import { default as axios, default as Axios } from "axios";
import type { ChangeEvent } from "react";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import { useTheme } from "styled-components/macro";
import useSWR from "swr";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { z } from "zod";
import { useAuthContext } from "../../../../../components/Auth";
import { Breadcrumb } from "../../../../../components/ProductsLayout/SharedProductsLayout";
import { LinkUnstyled } from "../../../../../components/LinkUnstyled/LinkUnstyled";
import {
  ButtonWithConfirmDialog,
  DeleteButton,
  DestructiveButtonLargeV2,
  LinkButtonLargeV2,
  TertiaryButtonMediumV2,
} from "../../../../../components/Buttons/Buttons";
import { DelayedSpinner } from "../../../../../components/DelayedSpinner/DelayedSpinner";
import { EditableTitle } from "../../../../../components/EditableTitle/EditableTitle";
import { ErrorPlaceholder } from "../../../../../components/Error";
import {
  CaretLeft,
  DeleteIcon,
  InfoIcon,
  LoadingIcon,
  PlusIcon,
  SystemDefaultIcon,
} from "../../../../../components/Icons/Icons";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import { SlideOut } from "../../../../../components/SlideOut/SlideOut";
import { Table } from "../../../../../components/Table/Table";
import {
  H3,
  SoftHeader2,
  SoftHeaderMedium,
} from "../../../../../components/Typography/Typography";
import { endpoints } from "../../../../../endpoints";
import { Flex } from "../../../../../layout/FormLayout";
import {
  PageWrapper,
  WhiteInnerCard,
} from "../../../../../layout/portalPageLayout";
import { ContentWrapper } from "../../../../../layout/publicPageLayout";
import type { TranslationsSchema, UUID } from "../../../../../types/types";
import type {
  AttributeGroupSummary,
  AttributeTemplateSchema,
} from "../../../../../types/types.PIM";
import { useRoutePath } from "../../../../../util/Routing";
import { isSystemProtectedAttribute } from "../../../../../util/attributeEditability";
import { useDebouncedRowReorder } from "../../../../../util/hooks";
import {
  removeUnderscore,
  rowHover,
  toTitleCase,
  useFormWrapper,
  useHasMultipleLanguages,
  useStoreState,
} from "../../../../../util/util";
import { zodRequiredString } from "../../../../../util/zod.util";
import { AttributesNav } from "../components/AttributesNav";
import {
  PageContentWrapper,
  SystemIconWrapper,
} from "../components/PIM.components.util";
import {
  CheckBoxNoMargin,
  HeaderWrapper,
} from "../SellerAdminPIMAttributesCollections/SellerAdminAttributesCollectionDetails/SellerAdminAttributesCollectionDetails";
import {
  TemplatesNav,
  TemplatesNavWithPadding,
} from "../SellerAdminTemplates/TemplatesNav";
import { AddAttributeToExistingGroup } from "./AddAttributeToExistingGroup";
import { GroupTranslations } from "./GroupTranslations";
import { ReferenceEntityMappingTable } from "../../../../../components/ReferenceEntityMappingTable";
import { shouldShowObjectReferenceAttribute } from "../../../../../components/ObjectReference/utils";
import { AttributeTypeDisplayOnList } from "../../../../../components/ObjectReference/AttributeTypeDisplay";
import { useMdmEntityDisplayNames } from "../../../../../util/mdmEntityDisplayNames";
import { spacingTokens } from "../../../../../designTokens";
import { AccordionCard } from "../../../../../components/AccordionCard/AccordionCard";
import { TextSmRegular } from "../../../../../components/Typography/TypographyV2";

const localstrings = (t: (s: string) => string) => ({
  tooltipAddAttributeSystemDefaultError: t(
    "You cannot add attributes to a system default group"
  ),
  tooltipDeleteAttributeSystemDefaultError: t(
    "You cannot remove attributes from a system default group"
  ),
  tooltipDeleteGroupSystemDefaultError: t(
    "You cannot remove a system default group"
  ),
  tooltipDeleteTemplateInUseError: t(
    "Products are using the template, please duplicate the template for any archival changes"
  ),
  tooltipDeleteProfileTemplateInUseError: t(
    "Profiles are using the template, please duplicate the template for any archival changes"
  ),
  tooltipEditTitleSystemDefaultError: t(
    "You cannot edit a system default group"
  ),
  tooltipEditTitleDefaultTemplateError: t(
    "You cannot edit default template title"
  ),
  tooltipEditTitlePermissionError: t(
    "You don't have permission to edit this title"
  ),
});

type AttributeType = {
  uuid: UUID;
  attribute_name: string;
  input_type: string;
  source_object_type?: string;
  target_object_type?: string;
  attribute_display_name: string;
  restricted: boolean;
  is_master: boolean;
  mandatory?: boolean;
  is_required: boolean;
  object_type?: string;
  is_system: boolean;
};

/** Row shape for the group's attribute table; identical to AttributeType. */
type GroupAttributeRow = AttributeType;

const isLastAttribute = (attributes: any[]) => attributes.length === 1;

const MandatoryCell = React.memo(function MandatoryCell({
  getValue,
  row,
  onUpdate,
}: {
  getValue: () => boolean;
  row: { original: { uuid: string; is_required?: boolean } };
  onUpdate: (uuid: string, isChecked: boolean) => Promise<void>;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const uuid = row.original.uuid;
  const isRequired = row.original.is_required;

  const handleCheckBoxChange = async (e: ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    try {
      await onUpdate(uuid, e.target.checked);
    } finally {
      setIsLoading(false);
    }
  };

  return isLoading ? (
    <span style={{ margin: "3px 15px 3px 0" }}>
      <LoadingIcon width={16} height={18} />
    </span>
  ) : (
    <CheckBoxNoMargin
      name={`mandatory-${uuid}`}
      checked={isRequired || getValue()}
      onChange={handleCheckBoxChange}
      disabled={isRequired}
    />
  );
});

const RestrictedOrMasterCell = React.memo(function RestrictedOrMasterCell({
  getValue,
  row,
  onUpdate,
}: {
  getValue: () => boolean;
  row: { original: { uuid: string; object_type?: string } };
  onUpdate: (uuid: string, isChecked: boolean) => Promise<void>;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const uuid = row.original.uuid;
  const shouldHideCheckbox = row.original.object_type === "product_name";

  if (shouldHideCheckbox) {
    return null;
  }

  const handleCheckBoxChange = async (e: ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    try {
      await onUpdate(uuid, e.target.checked);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading ? (
        <span style={{ margin: "3px 15px 3px 0" }}>
          <LoadingIcon width={16} height={18} />
        </span>
      ) : (
        <CheckBoxNoMargin
          name={`restricted-${uuid}`}
          checked={getValue()}
          onChange={handleCheckBoxChange}
        />
      )}
    </>
  );
});

const HeaderWithCheckboxComponent = React.memo(
  function RestrictedHeaderComponent({
    isHeaderLoading,
    isChecked,
    onHeaderChange,
    title,
    datatip_info,
    datatip_id,
  }: {
    isHeaderLoading: boolean;
    isChecked: boolean;
    onHeaderChange: (e: ChangeEvent<HTMLInputElement>) => Promise<void>;
    title: string;
    datatip_info: string;
    datatip_id: string;
  }) {
    const theme = useTheme();
    useTranslation();

    return (
      <HeaderWrapper>
        <>
          {isHeaderLoading ? (
            <LoadingIcon width={16} height={18} />
          ) : (
            <CheckBoxNoMargin
              name="restricted-all"
              checked={isChecked}
              onChange={onHeaderChange}
            />
          )}
        </>
        {title}
        <span data-tip={datatip_info} data-for={datatip_id}>
          <InfoIcon fill={theme.secondaryIconColor} width={14} height={14} />
        </span>
        <ReactTooltip
          id={datatip_id}
          delayHide={500}
          clickable
          effect="solid"
        />
      </HeaderWrapper>
    );
  }
);

/**
 * This page component is used in both the template groups section and the
 * regular groups section. One day the behavior might diverge enough that we
 * need to split this.
 *
 * differences in behavior:
 * 1. API calls in "template mode" modify the group inside the template
 * 2. when a group is modified from inside a template, the "modify_templates"
 *    permission applies. When modifying the group itself its "modify_groups"
 */
export function AttributeGroupDetail() {
  const {
    tenant_id,
    storefront_metadata: { object_reference_enabled },
  } = useStoreState();
  const hasMultipleLanguages = useHasMultipleLanguages();
  const { t } = useTranslation();
  const [tableData, setTableData] = useState<GroupAttributeRow[]>([]);
  const { getTemplateDisplayName } = useMdmEntityDisplayNames();
  const { group_id, template_id } =
    useParams<{ group_id: string; template_id?: string }>();
  const { adminPath } = useRoutePath();
  const [query] = useQueryParams({
    q: StringParam,
    offset: NumberParam,
    perPage: NumberParam,
  });

  const [showAddAttributeToGroupForm, setShowAddAttributeToGroupForm] =
    useState(false);

  const editTitleMethodsOfUseForm = useFormWrapper({
    resolver: zodResolver(
      z.object({
        title: zodRequiredString(t),
        type: z.enum(["name"]),
      })
    ),
    reValidateMode: "onChange",
    mode: "onChange",
  });

  const editDisplayNameMethodsOfUseForm = useFormWrapper({
    resolver: zodResolver(
      z.object({
        title: zodRequiredString(t),
        type: z.enum(["display_name"]),
      })
    ),
    reValidateMode: "onChange",
    mode: "onChange",
  });

  const { notifyError, notifySuccess } = useContext(Notifications);
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const titleRef = useRef<{ setEditMode: (editMode: boolean) => void }>(null);
  const displayNameRef =
    useRef<{ setEditMode: (editMode: boolean) => void }>(null);
  const isInTemplateSection = !!template_id;
  const { hasPermission } = useAuthContext();
  const {
    data: attributeGroupsResponse,
    error: attributesGroupsError,
    mutate: mutateGroup,
  } = useSWR<AttributeGroupSummary & { translations: TranslationsSchema[] }>(
    [
      template_id
        ? `/v2/tenants/${tenant_id}/pim/templates/${template_id}/groups/${group_id}/attributes`
        : `/v2/tenants/${tenant_id}/pim/groups/${group_id}/attributes`,
      useMemo(() => ({ params: { include_translations: true } }), []),
    ],
    {
      onSuccess: (data) =>
        editTitleMethodsOfUseForm.setValue("title", data.name),
    }
  );

  const { data: template, mutate: mutateTemplate } = useSWR<
    AttributeTemplateSchema,
    AxiosError
  >(
    template_id ? `/v2/tenants/${tenant_id}/pim/templates/${template_id}` : null
  );

  const hasModifyPermission =
    (isInTemplateSection && hasPermission("modify_templates")) ||
    (!isInTemplateSection && hasPermission("modify_groups"));

  const hasDeletePermission = isInTemplateSection
    ? hasPermission("delete_templates")
    : hasPermission("delete_groups");

  const groupID = attributeGroupsResponse?.id;

  const showMandatoryHeader = template?.show_mandatory_completion_score;
  const showMasterHeader = template?.enable_variant_products ?? false;

  const isLoadingAttributes =
    !attributeGroupsResponse && !attributesGroupsError;

  const isTemplateInUse = template?.is_frozen ?? false;
  const isProductTemplate =
    template?.template_type === "product" || template?.template_type === null;
  const is_general_group = useMemo(
    () =>
      template?.template_name === "Default Template" &&
      attributeGroupsResponse?.name === "General",
    [attributeGroupsResponse?.name, template?.template_name]
  );

  // Hoisted so per-row tooltip lookups (e.g. deleteAttributeDataTip, called
  // once per row on every render) don't re-run all of localstrings' t() calls
  // just to read one string.
  const strings = useMemo(() => localstrings(t), [t]);

  const deleteAttributeDataTip = useCallback(
    (is_system: boolean) => {
      if (isTemplateInUse) {
        return isProductTemplate
          ? strings.tooltipDeleteTemplateInUseError
          : strings.tooltipDeleteProfileTemplateInUseError;
      } else if (
        isSystemProtectedAttribute(
          attributeGroupsResponse?.is_system,
          is_system
        )
      ) {
        return strings.tooltipDeleteAttributeSystemDefaultError;
      } else {
        return "";
      }
    },
    [
      attributeGroupsResponse?.is_system,
      isTemplateInUse,
      isProductTemplate,
      strings,
    ]
  );

  const deleteGroupDataTip = useCallback(() => {
    if (isTemplateInUse) {
      return isProductTemplate
        ? strings.tooltipDeleteTemplateInUseError
        : strings.tooltipDeleteProfileTemplateInUseError;
    }

    const isSystemGroup = attributeGroupsResponse?.is_system;
    const isGeneralGroupInTemplate = isInTemplateSection && is_general_group;

    if (isSystemGroup || isGeneralGroupInTemplate) {
      return strings.tooltipDeleteGroupSystemDefaultError;
    }

    return "";
  }, [
    attributeGroupsResponse?.is_system,
    isInTemplateSection,
    isProductTemplate,
    isTemplateInUse,
    is_general_group,
    strings,
  ]);
  const handleGroupDelete = useCallback(async () => {
    try {
      await axios.delete(`/v2/tenants/${tenant_id}/pim/groups/${groupID}`);
      notifySuccess(t("Group removed successfully"));
      isInTemplateSection
        ? history.push(`${adminPath}/pim/templates/${template_id}/groups`)
        : history.push(`${adminPath}/pim/attributes/groups`);
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t("There was an error deleting the group"),
        {
          error,
        }
      );
    }
  }, [
    adminPath,
    groupID,
    history,
    isInTemplateSection,
    notifyError,
    notifySuccess,
    t,
    template_id,
    tenant_id,
  ]);

  const [isSavingReorder, setIsSavingReorder] = useState(false);
  const reorderedRowsRef = useRef<AttributeType[]>([]);

  const persistAttributesReorder = useCallback(async () => {
    setIsSavingReorder(true);
    const reorderList = reorderedRowsRef.current.map((row) => row.uuid);

    try {
      await Axios.post(
        `/v2/tenants/${tenant_id}/pim/groups/${group_id}/priority`,
        {
          items: reorderList,
        }
      );
      mutateTemplate();
      notifySuccess(t("Your changes have been saved successfully"));
    } catch (error) {
      notifyError(t("Something went wrong, please try again"), { error });
      attributeGroupsResponse &&
        setTableData(
          attributeGroupsResponse.attributes
            .filter((attr) =>
              shouldShowObjectReferenceAttribute({
                inputType: attr.input_type,
                objectReferenceEnabled: object_reference_enabled,
              })
            )
            .map((attr) => ({
              uuid: attr.id,
              attribute_name: attr.name,
              input_type: attr.input_type,
              source_object_type: (attr as { source_object_type?: string })
                .source_object_type,
              target_object_type: (attr as { target_object_type?: string })
                .target_object_type,
              attribute_display_name: attr.display_name
                ? attr.display_name
                : "",
              restricted: attr.is_restricted,
              is_master: attr.is_master,
              mandatory: showMandatoryHeader ? attr.is_mandatory : undefined,
              is_required: attr.is_required,
              object_type: attr.object_type,
              is_system: attr.is_system,
            }))
        );
    } finally {
      setIsSavingReorder(false);
    }
  }, [
    tenant_id,
    group_id,
    notifySuccess,
    notifyError,
    t,
    mutateTemplate,
    attributeGroupsResponse,
    setTableData,
    object_reference_enabled,
    showMandatoryHeader,
  ]);

  const { handleRowReorder, handleDrop: handleAttributesReorder } =
    useDebouncedRowReorder(reorderedRowsRef, persistAttributesReorder);

  useEffect(() => {
    const handleGroupData = (data: AttributeGroupSummary) => {
      setTableData(
        data.attributes
          .filter((attr) =>
            shouldShowObjectReferenceAttribute({
              inputType: attr.input_type,
              objectReferenceEnabled: object_reference_enabled,
            })
          )
          .map((attr) => ({
            uuid: attr.id,
            attribute_name: attr.name,
            input_type: attr.input_type,
            source_object_type: (attr as { source_object_type?: string })
              .source_object_type,
            target_object_type: (attr as { target_object_type?: string })
              .target_object_type,
            attribute_display_name: attr.display_name ? attr.display_name : "",
            restricted: attr.is_restricted,
            mandatory: showMandatoryHeader ? attr.is_mandatory : undefined,
            is_master: attr.is_master,
            is_required: attr.is_required,
            object_type: attr.object_type,
            is_system: attr.is_system,
          }))
      );
    };

    if (attributeGroupsResponse) {
      handleGroupData(attributeGroupsResponse);
    }
  }, [
    attributeGroupsResponse,
    setTableData,
    showMandatoryHeader,
    t,
    object_reference_enabled,
  ]);

  const [isMandatoryHeaderLoading, setIsMandatoryHeaderLoading] =
    useState(false);
  const [isRestrictedHeaderLoading, setIsRestrictedHeaderLoading] =
    useState(false);
  const [isMasterHeaderLoading, setIsMasterHeaderLoading] = useState(false);

  const tableColumns = React.useMemo<ColumnDef<AttributeType>[]>(() => {
    const handleDeleteAttributeFromGroup = async (uuid: UUID) => {
      try {
        const requestBody = { attribute_ids: [uuid] };
        await axios.delete(
          `/v2/tenants/${tenant_id}/pim/groups/${groupID}/attributes`,
          { data: { ...requestBody } }
        );
        notifySuccess(t("attribute removed successfully"));
        if (isLastAttribute(attributeGroupsResponse?.attributes ?? [])) {
          await handleGroupDelete();
        } else {
          mutateGroup();
        }
      } catch (error) {
        const errorMessage = (error as AxiosError)?.response?.data?.message;
        notifyError(
          errorMessage ? errorMessage : t("Failed to remove attribute"),
          {
            error,
          }
        );
      }
    };

    const is_delete_disabled = (original: AttributeType) => {
      const restricted_attributes = ["product_name", "product_id"];
      if (
        restricted_attributes.includes(original.object_type ?? "") &&
        is_general_group
      ) {
        return true;
      }
      return (
        isSystemProtectedAttribute(
          attributeGroupsResponse?.is_system,
          original.is_system
        ) || template?.is_frozen
      );
    };

    return isInTemplateSection
      ? [
          {
            header: t("Attribute Name"),
            accessorKey: "attribute_name",
          },
          {
            header: () => (
              <div style={{ textAlign: "left" }}>{t("Attribute Type")}</div>
            ),
            accessorKey: "input_type",
            cell: ({
              getValue,
              row,
            }: {
              getValue: Getter<string>;
              row: {
                original: {
                  input_type: string;
                  source_object_type?: string;
                  target_object_type?: string;
                };
              };
            }) => (
              <div style={{ textAlign: "left" }}>
                {toTitleCase(removeUnderscore(getValue() as string))}
                {row.original.input_type === "object_reference" && (
                  <AttributeTypeDisplayOnList
                    sourceObjectType={row.original.source_object_type}
                    targetObjectType={row.original.target_object_type}
                  />
                )}
              </div>
            ),
          },
          {
            header: t("Attribute Display Name"),
            accessorKey: "attribute_display_name",
          },
          {
            header: () => (
              <HeaderWithCheckboxComponent
                title={t("Restricted")}
                datatip_info={t(
                  "Checking the box will make the attribute restricted. Restricted attributes are only visible to internal users."
                )}
                datatip_id="restricted-header-tip"
                isHeaderLoading={isRestrictedHeaderLoading}
                isChecked={
                  attributeGroupsResponse?.attributes
                    .filter(
                      (attr) =>
                        attr.object_type !== "product_name" &&
                        attr.object_type !== "product_id"
                    )
                    .every((attr) => attr.is_restricted) ?? false
                }
                onHeaderChange={async (e: ChangeEvent<HTMLInputElement>) => {
                  const isChecked = e.target.checked;
                  setIsRestrictedHeaderLoading(true);
                  try {
                    await axios.patch<
                      { attribute_ids: "all" | string[] },
                      AttributeTemplateSchema
                    >(
                      endpoints.v2_tenants_id_or_slug_pim_templates_id_groups_id_attributes_restricted(
                        tenant_id,
                        template!.template_id,
                        attributeGroupsResponse!.id
                      ),
                      isChecked
                        ? { attributes_to_restrict: "all" }
                        : { attributes_to_unrestrict: "all" }
                    );
                    setTableData((prevData) =>
                      prevData.map((row) => ({
                        ...row,
                        restricted: isChecked,
                      }))
                    );
                    await mutateTemplate();
                    await mutateGroup();
                  } finally {
                    setIsRestrictedHeaderLoading(false);
                  }
                }}
              />
            ),
            accessorKey: "restricted",
            enableSorting: false,
            cell: (props) => (
              <RestrictedOrMasterCell
                getValue={() => props.getValue() as boolean}
                row={props.row}
                onUpdate={async (uuid, isChecked) => {
                  const patchRequestBody = isChecked
                    ? { attributes_to_restrict: [uuid] }
                    : { attributes_to_unrestrict: [uuid] };
                  try {
                    await axios.patch<
                      {
                        attributes_to_restrict?: string[];
                        attributes_to_unrestrict?: string[];
                      },
                      AttributeTemplateSchema
                    >(
                      endpoints.v2_tenants_id_or_slug_pim_templates_id_groups_id_attributes_restricted(
                        tenant_id,
                        template!.template_id,
                        attributeGroupsResponse!.id
                      ),
                      patchRequestBody
                    );
                    setTableData((prevData) =>
                      prevData.map((row) =>
                        row.uuid === uuid
                          ? { ...row, restricted: isChecked }
                          : row
                      )
                    );
                    await mutateTemplate();
                    await mutateGroup();
                  } catch (error) {
                    const errorMessage = (error as AxiosError)?.response?.data
                      ?.message;
                    notifyError(
                      errorMessage
                        ? errorMessage
                        : t(
                            "Could not update restricted status. Please try again later."
                          ),
                      { error }
                    );
                  }
                }}
              />
            ),
          },
          ...(showMandatoryHeader
            ? [
                {
                  header: () => (
                    <HeaderWithCheckboxComponent
                      title={t("Mandatory")}
                      datatip_info={t(
                        "Checking the box will make the attribute mandatory. Mandatory attributes are required for the product to be created."
                      )}
                      datatip_id="mandatory-header-tip"
                      isHeaderLoading={isMandatoryHeaderLoading}
                      isChecked={attributeGroupsResponse?.is_mandatory ?? false}
                      onHeaderChange={async (e) => {
                        const isChecked = e.target.checked;
                        setIsMandatoryHeaderLoading(true);
                        try {
                          await axios.patch<
                            { attribute_ids: "all" | string[] },
                            AttributeTemplateSchema
                          >(
                            endpoints.v2_tenants_id_or_slug_pim_templates_id_groups_id_attributes_mandate(
                              tenant_id,
                              template!.template_id,
                              attributeGroupsResponse!.id
                            ),
                            isChecked
                              ? { attributes_to_mandate: "all" }
                              : { attributes_to_unmandate: "all" }
                          );
                          setTableData((prevData) =>
                            prevData.map((row) => ({
                              ...row,
                              mandatory: isChecked,
                            }))
                          );
                          await mutateTemplate();
                          await mutateGroup();
                        } catch (error) {
                          const errorMessage = (error as AxiosError)?.response
                            ?.data?.message;
                          notifyError(
                            errorMessage
                              ? errorMessage
                              : t(
                                  "Could not update mandatory status. Please try again later."
                                ),
                            { error }
                          );
                        } finally {
                          setIsMandatoryHeaderLoading(false);
                        }
                      }}
                    />
                  ),
                  accessorKey: "mandatory",
                  enableSorting: false,
                  cell: (props: {
                    getValue: Getter<boolean>;
                    row: { original: { uuid: string } };
                  }) => (
                    <MandatoryCell
                      getValue={() => props.getValue() as boolean}
                      row={props.row}
                      onUpdate={async (uuid, isChecked) => {
                        const patchRequestBody = isChecked
                          ? { attributes_to_mandate: [uuid] }
                          : { attributes_to_unmandate: [uuid] };
                        try {
                          await axios.patch<
                            {
                              attributes_to_mandate?: string[];
                              attributes_to_unmandate?: string[];
                            },
                            AttributeTemplateSchema
                          >(
                            endpoints.v2_tenants_id_or_slug_pim_templates_id_groups_id_attributes_mandate(
                              tenant_id,
                              template!.template_id,
                              attributeGroupsResponse!.id
                            ),
                            patchRequestBody
                          );
                          setTableData((prevData) =>
                            prevData.map((row) =>
                              row.uuid === uuid
                                ? { ...row, mandatory: isChecked }
                                : row
                            )
                          );
                          await mutateTemplate();
                          await mutateGroup();
                        } catch (error) {
                          const errorMessage = (error as AxiosError)?.response
                            ?.data?.message;
                          notifyError(
                            errorMessage
                              ? errorMessage
                              : t(
                                  "Could not update mandatory status. Please try again later."
                                ),
                            { error }
                          );
                        }
                      }}
                    />
                  ),
                },
              ]
            : []),
          ...(showMasterHeader
            ? [
                {
                  header: () => (
                    <HeaderWithCheckboxComponent
                      title={t("Master")}
                      datatip_info={t(
                        "Checking the box will make the attribute a master attribute."
                      )}
                      datatip_id="master-header-tip"
                      isHeaderLoading={isMasterHeaderLoading}
                      isChecked={
                        attributeGroupsResponse?.attributes
                          .filter(
                            (attr) =>
                              attr.object_type !== "product_name" &&
                              attr.object_type !== "product_id"
                          )
                          .every((attr) => attr.is_master) ?? false
                      }
                      onHeaderChange={async (
                        e: ChangeEvent<HTMLInputElement>
                      ) => {
                        const isChecked = e.target.checked;
                        setIsMasterHeaderLoading(true);
                        try {
                          await axios.patch<
                            { attribute_ids: "all" | string[] },
                            AttributeTemplateSchema
                          >(
                            endpoints.v2_tenants_id_or_slug_pim_templates_id_groups_id_attributes_master(
                              tenant_id,
                              template!.template_id,
                              attributeGroupsResponse!.id
                            ),
                            isChecked ? { enable: "all" } : { disable: "all" }
                          );
                          setTableData((prevData) =>
                            prevData.map((row) => ({
                              ...row,
                              is_master: isChecked,
                            }))
                          );
                          await mutateTemplate();
                          await mutateGroup();
                        } catch (error) {
                          const errorMessage = (error as AxiosError)?.response
                            ?.data?.message;
                          notifyError(
                            errorMessage
                              ? errorMessage
                              : t(
                                  "Could not update to master attribute. Please try again later."
                                ),
                            { error }
                          );
                        } finally {
                          setIsMasterHeaderLoading(false);
                        }
                      }}
                    />
                  ),
                  accessorKey: "is_master",
                  enableSorting: false,
                  cell: (props: {
                    getValue: Getter<boolean>;
                    row: { original: { uuid: string; object_type?: string } };
                  }) => (
                    <RestrictedOrMasterCell
                      getValue={() => props.getValue() as boolean}
                      row={props.row}
                      onUpdate={async (uuid, isChecked) => {
                        const patchRequestBody = isChecked
                          ? { enable: [uuid] }
                          : { disable: [uuid] };
                        try {
                          await axios.patch<
                            {
                              enable?: string[];
                              disable?: string[];
                            },
                            AttributeTemplateSchema
                          >(
                            endpoints.v2_tenants_id_or_slug_pim_templates_id_groups_id_attributes_master(
                              tenant_id,
                              template!.template_id,
                              attributeGroupsResponse!.id
                            ),
                            patchRequestBody
                          );
                          setTableData((prevData) =>
                            prevData.map((row) =>
                              row.uuid === uuid
                                ? { ...row, is_master: isChecked }
                                : row
                            )
                          );
                          await mutateTemplate();
                          await mutateGroup();
                        } catch (error) {
                          const errorMessage = (error as AxiosError)?.response
                            ?.data?.message;
                          notifyError(
                            errorMessage
                              ? errorMessage
                              : t(
                                  "Could not update to master attribute. Please try again later."
                                ),
                            { error }
                          );
                        }
                      }}
                    />
                  ),
                },
              ]
            : []),
          {
            // The empty header and accessor are needed so it doesn't crash.
            header: "",
            accessorKey: " ",
            cell: ({
              row: { original },
            }: {
              row: { original: AttributeType };
            }) =>
              hasDeletePermission ? (
                <ButtonWithConfirmDialog
                  Button={(props) => (
                    <>
                      <DeleteButton
                        {...props}
                        datafor="attribute-delete-button"
                        datatip={deleteAttributeDataTip(original.is_system)}
                      />
                      <ReactTooltip id="attribute-delete-button" />
                    </>
                  )}
                  testid={"delete-individiual-attribute-from-group"}
                  disabled={
                    isInTemplateSection
                      ? is_delete_disabled(original)
                      : isSystemProtectedAttribute(
                          attributeGroupsResponse?.is_system,
                          original.is_system
                        ) || template?.is_frozen
                  }
                  handleConfirm={() =>
                    handleDeleteAttributeFromGroup(original.uuid)
                  }
                  confirmMessage={
                    isLastAttribute(attributeGroupsResponse!.attributes)
                      ? t(
                          "Are you sure you want to remove this attribute? This group will be removed."
                        )
                      : t("Are you sure you want to remove this attribute?")
                  }
                  title={t("Remove Attribute")}
                  confirmBtnLabel={t("Remove")}
                  confirmBtnType="destructive"
                  useV2Buttons={true}
                />
              ) : null,
          },
        ]
      : [
          {
            header: t("Attribute Name"),
            accessorKey: "attribute_name",
          },
          {
            // I feel like this wrapping in styled divs shoudln't be needed but this
            // is a way to make it look like the design.
            header: () => (
              <div style={{ textAlign: "left" }}>{t("Attribute Type")}</div>
            ),
            accessorKey: "input_type",
            cell: ({
              getValue,
              row,
            }: {
              getValue: Getter<string>;
              row: {
                original: {
                  input_type: string;
                  source_object_type?: string;
                  target_object_type?: string;
                };
              };
            }) => (
              <div style={{ textAlign: "left" }}>
                {toTitleCase(removeUnderscore(getValue() as string))}
                {row.original.input_type === "object_reference" && (
                  <AttributeTypeDisplayOnList
                    sourceObjectType={row.original.source_object_type}
                    targetObjectType={row.original.target_object_type}
                  />
                )}
              </div>
            ),
          },
          {
            header: t("Attribute Display Name"),
            accessorKey: "attribute_display_name",
          },
          {
            // The empty header and accessor are needed so it doesn't crash.
            header: "",
            accessorKey: " ",
            cell: ({
              row: { original },
            }: {
              row: { original: AttributeType };
            }) =>
              hasDeletePermission ? (
                <ButtonWithConfirmDialog
                  Button={(props) => (
                    <>
                      <DeleteButton
                        {...props}
                        datafor="attribute-delete-button"
                        datatip={deleteAttributeDataTip(original.is_system)}
                      />
                      <ReactTooltip id="attribute-delete-button" />
                    </>
                  )}
                  testid={"delete-individiual-attribute-from-group"}
                  disabled={
                    isInTemplateSection
                      ? is_delete_disabled(original)
                      : isSystemProtectedAttribute(
                          attributeGroupsResponse?.is_system,
                          original.is_system
                        ) || template?.is_frozen
                  }
                  handleConfirm={() =>
                    handleDeleteAttributeFromGroup(original.uuid)
                  }
                  confirmMessage={
                    isLastAttribute(attributeGroupsResponse!.attributes)
                      ? t(
                          "Are you sure you want to remove this attribute? This group will be removed."
                        )
                      : t("Are you sure you want to remove this attribute?")
                  }
                  title={t("Remove Attribute")}
                  confirmBtnLabel={t("Remove")}
                  confirmBtnType="destructive"
                  useV2Buttons={true}
                />
              ) : null,
          },
        ];
  }, [
    isInTemplateSection,
    t,
    showMandatoryHeader,
    showMasterHeader,
    tenant_id,
    groupID,
    notifySuccess,
    attributeGroupsResponse,
    handleGroupDelete,
    mutateGroup,
    notifyError,
    is_general_group,
    template,
    isRestrictedHeaderLoading,
    mutateTemplate,
    isMandatoryHeaderLoading,
    isMasterHeaderLoading,
    hasDeletePermission,
    deleteAttributeDataTip,
  ]);

  if (isLoadingAttributes) {
    return <DelayedSpinner />;
  }

  if (attributesGroupsError) {
    return (
      <ErrorPlaceholder message={"There was an error loading the group"} />
    );
  }

  if (attributeGroupsResponse) {
    const handleTitleEditConfirm = async ({
      title,
      type,
    }: {
      title: string;
      type: "name" | "display_name";
    }) => {
      const canSubmit = () =>
        (type === "name" && title.trim() !== attributeGroupsResponse.name) ||
        (type === "display_name" &&
          title.trim() !== attributeGroupsResponse.display_name);

      if (canSubmit()) {
        try {
          await axios.patch(
            `/v2/tenants/${tenant_id}/pim/groups/${groupID}/summary`,
            type === "name"
              ? {
                  name: title.trim(),
                  description: attributeGroupsResponse.description ?? "",
                }
              : {
                  display_name: title.trim(),
                  description: attributeGroupsResponse.description ?? "",
                }
          );
          // success notification intentionally left out because this is "inline"
          if (type === "name") {
            titleRef.current?.setEditMode(false);
          } else {
            displayNameRef.current?.setEditMode(false);
          }
        } catch (error) {
          const errorMessage = (error as AxiosError)?.response?.data?.message;
          notifyError(
            errorMessage ? errorMessage : t("Error editing group title"),
            {
              error,
            }
          );
        }
        mutateGroup();
      }
    };

    const params = new URLSearchParams();
    if (query.q) {
      params.append("q", query.q);
    }
    params.append("offset", String(query?.offset ?? 0));
    params.append("perPage", String(query?.perPage ?? 10));

    return (
      <PageWrapper>
        <Breadcrumb
          style={{ padding: `${spacingTokens.spacing_xl}`, paddingBottom: 0 }}
        >
          {isInTemplateSection ? (
            <>
              <LinkUnstyled to={`${adminPath}/pim/templates`}>
                {t("Templates")}
              </LinkUnstyled>
              <div>/</div>
              <LinkUnstyled
                to={`${adminPath}/pim/templates/${template_id}/groups`}
              >
                {template ? getTemplateDisplayName(template) : t("Template")}
              </LinkUnstyled>
            </>
          ) : (
            <LinkUnstyled to={`${adminPath}/pim/attributes/groups?${params}`}>
              {t("Groups")}
            </LinkUnstyled>
          )}
        </Breadcrumb>
        <TemplatesNavWithPadding
          style={{ marginBottom: spacingTokens.spacing_xl }}
        >
          {isInTemplateSection ? (
            <TemplatesNav
              pageTitle={template?.template_name ?? ""}
              tabIndex={0}
              templateID={template_id!}
              enableCustomForms={template?.enable_custom_forms}
            />
          ) : (
            <AttributesNav tabName={"Groups"} params={params} />
          )}
        </TemplatesNavWithPadding>
        <div
          style={{
            width: "fit-content",
            padding: `0 ${spacingTokens.spacing_lg}`,
          }}
        >
          <Link
            to={
              location.search.includes("from=dashboard")
                ? `${adminPath}/dashboard`
                : isInTemplateSection
                ? `${adminPath}/pim/templates/${template_id}/groups?${params}`
                : `${adminPath}/pim/attributes/groups?${params}`
            }
          >
            <LinkButtonLargeV2 style={{ padding: "0 0 20px" }}>
              <CaretLeft fill="currentColor" />
              {t("Groups")}
            </LinkButtonLargeV2>
          </Link>
        </div>
        <PageContentWrapper>
          <WhiteInnerCard
            style={{ marginBottom: theme.resolve("spacing_3xl") }}
          >
            <AccordionCard
              title={t("Group Definition")}
              hideHeaderBorder={true}
              actionChildren={() => (
                <div style={{ alignSelf: "flex-end" }}>
                  {attributeGroupsResponse.is_system ? (
                    <Flex
                      style={{
                        justifyContent: "end",
                        alignItems: "center",
                        flexWrap: "nowrap",
                        margin: "0 0 15px",
                      }}
                    >
                      <SystemIconWrapper
                        data-for={`system${template_id}`}
                        data-tip={t("System Default")}
                      >
                        <SystemDefaultIcon width={22} height={22} />
                      </SystemIconWrapper>
                      <SoftHeader2 style={{ whiteSpace: "nowrap" }}>
                        {t("System Default")}
                      </SoftHeader2>
                    </Flex>
                  ) : null}
                </div>
              )}
              expanded={true}
            >
              <ContentWrapper>
                <WhiteInnerCard
                  style={{ marginTop: theme.resolve("spacing_xl") }}
                >
                  <AccordionCard
                    title={() =>
                      isInTemplateSection ? (
                        <>
                          <H3 style={{ margin: 0 }}>
                            {attributeGroupsResponse.name}
                          </H3>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: spacingTokens.spacing_md,
                            }}
                          >
                            <TextSmRegular color="secondary">
                              {t("Display name:")}
                            </TextSmRegular>
                            <TextSmRegular color="primary">
                              {attributeGroupsResponse.display_name
                                ? t([attributeGroupsResponse.display_name])
                                : attributeGroupsResponse.name}
                            </TextSmRegular>
                          </div>
                        </>
                      ) : (
                        <>
                          <form
                            id="edit-title-form"
                            noValidate
                            onSubmit={editTitleMethodsOfUseForm.handleSubmit(
                              handleTitleEditConfirm
                            )}
                          >
                            <EditableTitle
                              title={attributeGroupsResponse.name}
                              formId="edit-title-form"
                              name="title"
                              methodsOfUseForm={editTitleMethodsOfUseForm}
                              disabled={
                                attributeGroupsResponse.is_system ||
                                !hasModifyPermission
                              }
                              datatip={(() => {
                                if (attributeGroupsResponse.is_system) {
                                  return strings.tooltipEditTitleSystemDefaultError;
                                } else if (!hasModifyPermission) {
                                  return strings.tooltipEditTitlePermissionError;
                                } else return "";
                              })()}
                              ref={titleRef}
                              stopClickPropagation
                            />
                            <input
                              name="type"
                              ref={editTitleMethodsOfUseForm.register}
                              value="name"
                              type="hidden"
                            />
                          </form>
                          <form
                            id="edit-displayname-form"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: spacingTokens.spacing_md,
                            }}
                            noValidate
                            onSubmit={editDisplayNameMethodsOfUseForm.handleSubmit(
                              handleTitleEditConfirm
                            )}
                          >
                            <SoftHeaderMedium>
                              {t("Display name: ")}
                            </SoftHeaderMedium>
                            <EditableTitle
                              title={
                                attributeGroupsResponse.display_name
                                  ? t([attributeGroupsResponse.display_name])
                                  : "--"
                              }
                              formId="edit-displayname-form"
                              name="title"
                              methodsOfUseForm={editDisplayNameMethodsOfUseForm}
                              disabled={!hasPermission("modify_groups")}
                              datatip={
                                !hasPermission("modify_groups")
                                  ? strings.tooltipEditTitlePermissionError
                                  : ""
                              }
                              ref={displayNameRef}
                              fontSize="regular"
                              fontWeight="regular"
                              stopClickPropagation
                            />
                            <input
                              name="type"
                              ref={editDisplayNameMethodsOfUseForm.register}
                              value="display_name"
                              type="hidden"
                            />
                          </form>
                        </>
                      )
                    }
                    hideHeaderBorder={true}
                    actionChildren={() => <></>}
                    expanded={true}
                  >
                    <div
                      style={{
                        marginTop: theme.resolve("spacing_lg"),
                      }}
                    >
                      <Table
                        columns={tableColumns}
                        isLoading={isLoadingAttributes}
                        error={attributesGroupsError}
                        data={tableData}
                        rowHover={rowHover}
                        enableReorder={isInTemplateSection && !isSavingReorder}
                        handleRowReorder={handleRowReorder}
                        handleDrop={handleAttributesReorder}
                      />
                    </div>
                    <>
                      {hasModifyPermission && (
                        <TertiaryButtonMediumV2
                          onClick={() => setShowAddAttributeToGroupForm(true)}
                          style={{ marginTop: theme.resolve("spacing_xl") }}
                        >
                          <PlusIcon
                            width={20}
                            height={20}
                            fill={theme.primaryButtonTextColor}
                          />
                          {t("Add attribute")}
                        </TertiaryButtonMediumV2>
                      )}
                    </>
                    {hasMultipleLanguages && !isInTemplateSection ? (
                      <div style={{ marginTop: spacingTokens.spacing_4xl }}>
                        <GroupTranslations
                          group={attributeGroupsResponse}
                          mutate={mutateGroup}
                        />
                      </div>
                    ) : (
                      <></>
                    )}
                  </AccordionCard>
                </WhiteInnerCard>
              </ContentWrapper>
            </AccordionCard>
          </WhiteInnerCard>

          {isInTemplateSection &&
            attributeGroupsResponse.has_reference_entity_mapping && (
              <ReferenceEntityMappingTable
                contextType="group"
                contextId={group_id}
              />
            )}
          {hasDeletePermission && !attributeGroupsResponse.is_system && (
            <ButtonWithConfirmDialog
              Button={(props) => (
                <>
                  <DestructiveButtonLargeV2
                    {...props}
                    style={{ marginBottom: theme.resolve("spacing_3xl") }}
                  >
                    {<DeleteIcon />} {t("Remove Group")}
                  </DestructiveButtonLargeV2>
                  <ReactTooltip
                    id="remove-group-button"
                    effect="solid"
                    place="right"
                  />
                </>
              )}
              disabled={
                is_general_group ||
                attributeGroupsResponse.is_system ||
                template?.is_frozen
              }
              testid="remove-group"
              handleConfirm={handleGroupDelete}
              datafor="remove-group-button"
              useV2Buttons={true}
              datatip={deleteGroupDataTip()}
              confirmMessage={t("Are you sure you want to remove this group?")}
              confirmBtnLabel={t("Delete")}
              confirmBtnType="destructive"
            />
          )}
          <SlideOut
            title={t("Add Attributes")}
            show={showAddAttributeToGroupForm}
            closeFlyout={() => setShowAddAttributeToGroupForm(false)}
          >
            <AddAttributeToExistingGroup
              group={attributeGroupsResponse}
              isTemplateInUse={isTemplateInUse}
              templateEntityType={
                template_id ? template?.template_type ?? "product" : undefined
              }
              onSuccess={() => {
                mutateGroup();
                setShowAddAttributeToGroupForm(false);
              }}
              templateType={template?.template_type ?? null}
              onCancel={() => setShowAddAttributeToGroupForm(false)}
            />
          </SlideOut>
        </PageContentWrapper>
      </PageWrapper>
    );
  } else return null;
}
